<script lang="ts">
  import BoardTile from './BoardTile.svelte';
  import TracePath from './TracePath.svelte';
  import type { PowerUpKind } from '../../shared/power-ups';

  /**
   * A small, non-interactive board showing a trace: the tutorial illustrations and the
   * post-match recap. Tiles and line match the game board so every theme styles all three alike.
   */
  interface Props {
    board: readonly string[];
    size: number;
    /** Traced tiles in order; callers step through a word with `tracedTiles`. */
    traced: readonly number[];
    /** A power-up waiting on a tile: the line passes through it unselected so its treatment stays visible. */
    powerUp?: { tile: number; kind: PowerUpKind } | undefined;
  }
  let { board, size, traced, powerUp = undefined }: Props = $props();

  const selected = $derived(traced.filter((index) => index !== powerUp?.tile));
</script>

<div class="traced-board" style:--board-size={size}>
  <div class="board mini">
    {#each board as letter, index (index)}
      <BoardTile
        {index}
        {letter}
        revealed
        revealing={false}
        selected={selected.includes(index)}
        head={selected.at(-1) === index}
        powerUp={powerUp?.tile === index ? powerUp.kind : undefined}
      />
    {/each}
  </div>
  <TracePath path={traced} {size} />
</div>

<style>
  /* One size for every small board; letters scale with tile size. The shared 2% board gap keeps
     the trace line on tile centres. */
  .traced-board {
    --traced-board-width: 136px;
    position: relative;
    width: var(--traced-board-width);
    aspect-ratio: 1;
    margin: 0 auto;
    --tile-font-size: calc(var(--traced-board-width) / var(--board-size) * 0.55);
    --qu-font-size: calc(var(--traced-board-width) / var(--board-size) * 0.45);
  }
  /* The game board claims every touch for tracing; a picture must let pages scroll and swipe. */
  .board {
    touch-action: auto;
  }
</style>
